// Import directly from the product-keys source file (not the re-exporting `../products`
// barrel) to avoid a circular import that leaves ProductKeys3PValues undefined when this
// module is loaded via certain quick-find code paths.
import type { ProductKeys3P } from '../product-keys';
import { ProductKeys3P as ProductKeys3PValues } from '../product-keys';

import type { TypeFilterValueKey } from './universal-type';
import { TypeFilterValueKey as TypeFilterValueKeyValues } from './universal-type';

import type { FilterId } from './index';

/**
 * Registry mapping each conditionable FilterId to its valid value type.
 * Only filters that can appear as conditions in exclusion rules need entries.
 *
 * To add a new conditionable filter, add it here and the compiler will
 * enforce correct values in all rules.
 */
interface FilterValueRegistry {
	universalTypeFilter: TypeFilterValueKey;
	// Add more conditionable filters here as needed, e.g.:
	// statusFilter: StatusFilterValue;
	// lastUpdated: LastUpdatedValue;
}

/** Filter IDs that can appear as conditions (have known value types). */
type ConditionableFilterId = keyof FilterValueRegistry;

/**
 * A type-safe condition tuple: [filterId, filterValue].
 */
export type FilterCondition<F extends ConditionableFilterId = ConditionableFilterId> =
	F extends unknown ? [F, FilterValueRegistry[F]] : never;

/** At least one element — prevents vacuous rules with empty arrays. */
type NonEmpty<T> = [T, ...T[]];

/**
 * Defines a rule for conditionally hiding filters.
 *
 * A rule activates when ALL of the following are true:
 * - If `selectedProduct` is specified, it matches the currently active product
 * - ALL `selectedFilters` conditions match (each [filterId, value] tuple is satisfied)
 *
 * When a rule activates, its `hiddenFilters` are removed from the filter bar and
 * the dropdown panel — unless a filter already has active selections. Filters that
 * the user has already interacted with (i.e., have selected values) are never hidden,
 * even if a rule would otherwise exclude them.
 *
 * For "show only when …" semantics (the inverse), use `FilterInclusionRule` in
 * `filter-inclusion-rules.ts` instead.
 *
 * Type safety enforcements:
 * - `selectedProduct` must be a valid `ProductKeys3P` value (no typos)
 * - Filter conditions enforce correct value types per filter ID
 * - `hiddenFilters` must have at least one entry (prevents no-op rules)
 *
 * @example
 * // Hide "lastUpdated" and "contributor" when Slack is selected AND
 * // the universalType filter has "conversation" selected:
 * {
 *   selectedProduct: ProductKeys3PValues.Slack,
 *   selectedFilters: [['universalTypeFilter', TypeFilterValueKeyValues.Conversation]],
 *   hiddenFilters: ['lastUpdated', 'contributor'],
 * }
 *
 * @example
 * // Hide "contributor" for ANY universalType when Figma is selected:
 * {
 *   selectedProduct: ProductKeys3PValues.Figma,
 *   selectedFilters: [],
 *   hiddenFilters: ['contributor'],
 * }
 *
 * @example
 * // Hide "lastUpdated" whenever universalType is "conversation", regardless of product:
 * {
 *   selectedFilters: [['universalTypeFilter', TypeFilterValueKeyValues.Conversation]],
 *   hiddenFilters: ['lastUpdated'],
 * }
 */
export type FilterExclusionRule = {
	/** Optional: The 3P product that must be selected. */
	selectedProduct?: ProductKeys3P;
	/**
	 * Array of [FilterId, value] conditions. ALL must match for the rule to activate.
	 * An empty array means no filter-value conditions are required (only product match, if specified).
	 * Values are type-checked against the FilterValueRegistry.
	 */
	selectedFilters: FilterCondition[];
	/** Filter IDs to hide when this rule activates. Must have at least one entry. */
	hiddenFilters: NonEmpty<FilterId>;
};

/**
 * Centralized list of filter exclusion rules.
 *
 * Each rule defines a set of conditions (product + filter selections) and the
 * filters that should be hidden when ALL conditions are met.
 *
 * Multiple rules can match simultaneously — their `hiddenFilters` are merged (union).
 *
 * To add a new rule, append an entry to this array. Examples:
 *
 * ```typescript
 * // Hide "lastUpdated" when Teams + Conversation:
 * {
 *   selectedProduct: ProductKeys3PValues.Teams,
 *   selectedFilters: [['universalTypeFilter', TypeFilterValueKeyValues.Conversation]],
 *   hiddenFilters: ['lastUpdated'],
 * }
 * ```
 */
export const FILTER_EXCLUSION_RULES: FilterExclusionRule[] = [
	{
		selectedProduct: ProductKeys3PValues.Slack,
		selectedFilters: [['universalTypeFilter', TypeFilterValueKeyValues.Conversation]],
		hiddenFilters: ['lastUpdated', 'contributor'],
	},
];
